import {
  createElement,
  Fragment,
  ReactNode,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { TextColor, FontStyle, useDesignTokens, renderInlineCssVars } from '../../../theme';
import { SliderGrip, SliderGripRef } from './SliderGrip';
import { SliderInput } from './SliderInput';
import { SliderGripLabel } from '../GripLabel';
import { SliderScale } from '../SliderScale';
import { SliderFormat, calculatePreciseValue } from '../SliderFormat';
import { SliderTrackClientBounds } from '../useSliderDrag';
import { SliderStep } from '../SliderStep';
import { SliderGripInputRef, SliderGripInputRenderProps } from './SliderGripInputProps';
import { GripType } from '../SliderValues';
import { BaseSliderProps } from '../__internal__';
import { postfixTestId, TestIdProps } from '../../../util/testId';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useFormatLocale } from '../../../locale/formatting';
import { useKeyboardTooltip } from '../../Settings';
import { isTruncated } from '../../../hooks/useTruncationTooltip';
import { setRef } from '../../../util/ref-util';

export interface SliderValueGripProps extends TestIdProps {
  gripLabelClassName: string | undefined;
  clientBounds: SliderTrackClientBounds;
  orientation: 'horizontal' | 'vertical';
  arrowGrips: boolean;
  value: number;
  minValue: number;
  maxValue: number;
  scale: SliderScale;
  format: SliderFormat;
  defaultFormat: SliderFormat;
  disableRoundToStep?: boolean;
  isRange?: boolean;
  hideLabel: boolean;
  hideInput: boolean;
  type: GripType;
  ariaLabel: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  inputAriaLabel: string;
  sliderHasTicks?: boolean;

  zIndex: number;
  inclusive: boolean;

  onDragStart?: () => void;
  onChange?: (event: { value: number; didDrag?: boolean }) => void;
  onCommit?: (event: { value: number; didDrag?: boolean }) => void;

  stepValue: SliderStep;
  stepMediumValue: SliderStep;
  stepLargeValue: SliderStep;

  disabled?: boolean;
  readOnly?: boolean;

  renderGripInput?: (props: SliderGripInputRenderProps) => ReactNode;

  handleLabelTokens:
    | undefined
    | null
    | {
        theme?: () => Partial<TextColor> | undefined;
        font?: Partial<FontStyle>;
      };
  getSliderClientRect: () => DOMRect | undefined;
  setSliderGripControlIsVisible?: BaseSliderProps['setSliderGripControlIsVisible'];
}

export interface SliderValueGripRef {
  getGripContainerWidth(): number | undefined;
  getGripLabelElementWidth(): number | undefined;
}

const useHtmlId = createUseHtmlId('sliderGrip');

export const SliderValueGrip = forwardRef<SliderValueGripRef, SliderValueGripProps>(
  (props, ref) => {
    const { orientation } = props;
    const dt = useDesignTokens();
    const tokens = renderInlineCssVars(
      {
        'spacing-squish-xs': dt.spacing.insetSquish.xs.padding,
        'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
      },
      'slider-value-grip'
    );

    const refInput = useRef<SliderGripInputRef | null>(null);
    const refGrip = useRef<SliderGripRef | null>(null);
    const refGripLabel = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (orientation === 'vertical' && refGripLabel.current) {
        const paddingStart =
          parseInt(window.getComputedStyle(refGripLabel.current).paddingInlineStart) ?? 0;
        const inputWidth = refInput.current?.getInputWidth?.() ?? 0;
        refGripLabel.current.style.minWidth = `${inputWidth + paddingStart}px`;
      }
    }, [orientation]);

    useImperativeHandle(ref, () => ({
      getGripContainerWidth() {
        return refGrip.current?.getGripContainerElement()?.getBoundingClientRect().width;
      },
      getGripLabelElementWidth() {
        return refGripLabel.current?.getBoundingClientRect().width;
      },
    }));

    const position = props.scale.position(props.value);

    const renderGripInput =
      props.renderGripInput ??
      ((p: SliderGripInputRenderProps) => <SliderInput {...p} ref={p.ref} />);

    const shouldHideInput = props.disabled || props.readOnly || props.hideInput;

    const invisibleTextId = useHtmlId();
    const ariaLabelledBy = props.ariaLabelledBy
      ? props.ariaLabelledBy.concat(' ', invisibleTextId)
      : invisibleTextId;

    const numberFormat = Intl.NumberFormat(useFormatLocale());

    const [keyboardTooltip, setKeyboardTooltip] = useState('');
    const keyboardRef = useKeyboardTooltip({ tooltip: keyboardTooltip });

    useEffect(() => {
      const labelWrapper = document.getElementById(props.ariaDescribedBy ?? '');
      const label = labelWrapper ? labelWrapper.getElementsByTagName('label')[0] : null;
      const labelIsTruncated = label ? isTruncated(label) : false;

      if (label && labelIsTruncated) {
        const labelText = label.innerText;
        setKeyboardTooltip(labelText);
      } else {
        setKeyboardTooltip('');
      }
    }, [props.ariaDescribedBy]);

    setRef(keyboardRef, refGrip.current?.getGripElement());

    return (
      <Fragment>
        <SliderGrip
          testId={props.testId}
          ariaLabel={props.ariaLabel}
          ariaLabelledBy={ariaLabelledBy}
          ariaDescribedBy={props.ariaDescribedBy}
          disabled={props.disabled}
          readOnly={props.readOnly}
          ref={refGrip}
          value={props.value}
          formattedValue={props.format.format(props.value)}
          minValue={props.minValue}
          maxValue={props.maxValue}
          type={props.type}
          scale={props.scale}
          clientBounds={props.clientBounds}
          orientation={props.orientation}
          arrowGrips={props.arrowGrips}
          inclusive={props.inclusive}
          zIndex={props.zIndex}
          sliderHasTicks={props.sliderHasTicks}
          onDragStart={props.onDragStart}
          onChange={
            props.disabled || props.readOnly
              ? undefined
              : ({ position, didDrag }) => {
                  props.onChange && props.onChange({ value: props.scale.value(position, didDrag) });
                }
          }
          onCommit={
            props.disabled || props.readOnly
              ? undefined
              : ({ position, didDrag }) => {
                  props.onCommit && props.onCommit({ value: props.scale.value(position, didDrag) });
                }
          }
          onClick={
            shouldHideInput
              ? undefined
              : () => {
                  refInput.current && refInput.current.focusInput();
                }
          }
          onKeyAction={
            props.disabled || props.readOnly
              ? undefined
              : ({ type, dir }) => {
                  let value: number;
                  if (type === 'stepMax') {
                    value = dir === 'forward' ? props.maxValue : props.minValue;
                  } else {
                    let step: SliderStep = 0;
                    if (type === 'step') {
                      step = props.stepValue;
                    } else if (type === 'stepTwo') {
                      step = props.stepMediumValue;
                    } else if (type === 'stepLarge') {
                      step = props.stepLargeValue;
                    }

                    value = props.value;
                    if (typeof step === 'number') {
                      value += (dir === 'forward' ? 1 : -1) * step;
                      value = calculatePreciseValue(
                        value,
                        props.stepValue,
                        numberFormat,
                        props.disableRoundToStep
                      );
                    } else {
                      value = step(value, { type: dir });
                    }
                    if (value > props.maxValue) {
                      value = props.maxValue;
                    } else if (value < props.minValue) {
                      value = props.minValue;
                    }
                  }

                  props.onChange && props.onChange({ value });
                  props.onCommit && props.onCommit({ value });
                }
          }
        />
        <SliderGripLabel
          ref={refGripLabel}
          testId={postfixTestId(props.testId, 'label')}
          disabled={props.disabled}
          className={props.gripLabelClassName}
          position={position}
          orientation={props.orientation}
          label={props.format.format(props.value)}
          handleLabelTokens={props.handleLabelTokens}
          hideLabel={props.hideLabel}
          isRange={props.isRange}
          onPress={
            shouldHideInput
              ? undefined
              : () => {
                  refInput.current && refInput.current.focusInput();
                }
          }
        >
          {/* only one value can be entered, so we don't render the inputs.
           * maybe we could stop them from showing and take them out of the tab order
           * https://gitlab.sas.com/dvr/html/react-commons/issues/262
           */}
          {props.scale.minValue === props.scale.maxValue || shouldHideInput
            ? null
            : renderGripInput({
                testId: props.testId,
                ref: refInput,
                orientation: props.orientation,
                position,
                value: props.value,
                format: props.format,
                defaultFormat: props.defaultFormat,
                step: props.stepValue,
                style: tokens,
                minValue: props.minValue,
                maxValue: props.maxValue,
                onCommit: ({ value }) => {
                  if (value < props.minValue) {
                    value = props.minValue;
                  } else if (value > props.maxValue) {
                    value = props.maxValue;
                  }
                  if (props.onChange && props.value !== value) props.onChange({ value });
                  props.onCommit && props.onCommit({ value });
                },
                onCancel: () => {
                  // Set focus to the grip above.
                  refGrip.current?.focusGrip();
                },
                type: props.type,
                ariaLabel: props.inputAriaLabel,
                getSliderClientRect: props.getSliderClientRect,
                setSliderGripControlIsVisible: props.setSliderGripControlIsVisible,
              })}
        </SliderGripLabel>
      </Fragment>
    );
  }
);
